<script setup lang="ts">
import { ActionSheet, Cascader, type CascaderOption, ConfigProvider, Field, Popup, Tabbar, TabbarItem } from 'vant';
import { computed, ref, watch } from 'vue';
import { useStyle3d } from '@repo/style3d';
import { useSelectionStore } from '@repo/stores';
import FabricScaleAngle from './fabric-scale-angle.vue';
import FabricMovement from './fabric-movement.vue';
import FabricColor from './fabric-color.vue';
import FabricRegionColor from './fabric-region-color.vue'
import { useParamStore } from '@/store/param.ts';
import { useI18n } from 'vue-i18n'

const { t, locale } = useI18n() // 用 t 代替 $t

const show = defineModel<boolean>('show');
const { selectMaterial, getMaterialList, getFabricState } = useStyle3d();

const selectionStore = useSelectionStore();
const paramStore = useParamStore();

const scale = ref(1);
const angle = ref(0);

watch(() => selectionStore.selectedFabricId, async (value) => {
  const list = await getMaterialList();
  fieldValue.value = list.find((f) => f.id === value)?.name || '';

  const { cycle = 1.0, rotation = 0 } = await getFabricState();
  angle.value = rotation;
  scale.value = cycle;
});

const options = ref<CascaderOption[]>([]);

watch([() => paramStore.recentUpdate, () => show.value, locale], async ([_, showVal]) => {
  if (showVal) {
    const list = await getMaterialList();

    const patternOptions = Array.from(paramStore.patternTypeMap)
      .filter(([_, type]) => ['pattern'].includes(type))
      .map(([id, _]) => id).map(id => list.find(item => item.id === id))
      .filter(item => !!item)
      .map(item => ({ text: item!.name, value: item!.id }));

    const attachmentOptions = Array.from(paramStore.patternTypeMap)
      .filter(([_, type]) => ['button', 'buttonHole', 'zipperSlider', 'zipperPullTeeth', 'zipperPiece'].includes(type))
      .map(([id, _]) => id).map(id => list.find(item => item.id === id))
      .filter(item => !!item)
      .map(item => ({ text: item!.name, value: item!.id }));

    const otherOptions = Array.from(paramStore.patternTypeMap)
      .filter(([_, type]) => !['pattern', 'button', 'buttonHole', 'zipperSlider', 'zipperPullTeeth', 'zipperPiece'].includes(type))
      .map(([id, _]) => id).map(id => list.find(item => item.id === id))
      .filter(item => !!item)
      .map(item => ({ text: item!.name, value: item!.id }));

    options.value = [
      {
        //text: '版片',
        text: t('fabric-editor.pattern'),
        value: '1',
        children: patternOptions
      },
      {
        //text: '附件',
        text: t('fabric-editor.accessories'),
        value: '2',
        children: attachmentOptions
      },
      {
        //text: '其它',
        text: t('fabric-editor.others'),
        value: '3',
        children: otherOptions
      }
    ];
  }
});

const tab = ref(0);

const title = computed(() => {
  const value = tab.value === 0 ?  t('fabric-editor.change-color') : t('fabric-editor.adjust');
  return value;
});

const fieldValue = ref('');
const showCascader = ref(false);
const cascaderValue = ref('1');


const onFinish = async ({ value }: { value: string }) => {
  showCascader.value = false;

  await selectMaterial(value, 400, true, 3);
};

const themeVars = {
  'cascader-options-height': '230px'
};
</script>

<template>
  <ActionSheet v-model:show="show" :title="title" :overlay="false" teleport="#app">
    <div :class="$style.content">
      <Field
        is-link
        readonly
        label=""
        v-model="fieldValue"
        :placeholder="$t('fabric-editor.select')"
        @click="showCascader = true"
      />
      <Popup v-model:show="showCascader" round position="bottom" teleport="#app">
        <ConfigProvider :theme-vars="themeVars">
          <Cascader
            v-model="cascaderValue"
            :title="$t('fabric-editor.select')"
            :placeholder="$t('fabric-editor.select')"
            :options="options"
            @close="showCascader = false"
            @finish="onFinish"
          />
        </ConfigProvider>
      </Popup>
      <FabricRegionColor v-if="tab===0" />
      <FabricColor v-if="tab===0" />
      <div v-else-if="tab===1" style="display: flex; justify-content: space-around;">
        <FabricMovement />
        <FabricScaleAngle v-model:scale="scale" v-model:angle="angle" />
      </div>
    </div>
    <Tabbar v-model="tab">
      <TabbarItem>{{ $t('fabric-editor.change-color') }}</TabbarItem>
      <TabbarItem>{{ $t('fabric-editor.adjust') }}</TabbarItem>
    </Tabbar>
  </ActionSheet>
</template>

<style module>
.content {
  padding: 10px 30px 60px;
  display: flex;
  flex-direction: column;
  min-height: 230px;
}
</style>

